import { StrictMode } from 'react'
import type { VFC } from 'react'
import { storyfirstUnitDisplayName } from '@opallabs/concepts'
import { Size } from '@opallabs/gem'
import { Conditional, Tooltip } from '@opallabs/kuiper'
import {
  capitalCase,
  formatCurrencyAmount,
  isPositiveNumber,
} from '@opallabs/lambda'
import { Icon, screenreaderOnly } from '@opallabs/lapidary'
import { PopoverContent } from './PopoverContent'
import * as styles from './styles'
import type { BudgetHeaderProps } from './types'

export const formatBudget = (value: number, currency = 'USD'): string => {
  if (!isPositiveNumber(value)) return '—'

  return formatCurrencyAmount({
    compactNotation: true,
    currency,
    value,
  })
}

/**
 * Display the amount of money available to allocate (`max`) for a given
 * `context` and `budgetType`.
 *
 * If `showInfoTooltip` is `true` an info tooltip is rendered that explains
 * how budgeting works.
 */
export const BudgetHeader: VFC<BudgetHeaderProps> = ({
  ariaHidden = false,
  budgetType,
  context,
  currency = 'USD',
  htmlId,
  max = 0,
  showInfoTooltip = false,
  styleOverrides = {},
  theme = 'light',
  title,
}) => (
  <StrictMode>
    <div
      aria-hidden={ariaHidden}
      css={[styles.container({ theme }), styleOverrides]}
      id={htmlId}
      role="group"
    >
      <div css={styles.title}>
        {title && <span aria-hidden>{capitalCase(title)}</span>}

        <Conditional condition={showInfoTooltip}>
          <Tooltip
            position="top"
            content={<PopoverContent context={context} theme={theme} />}
            theme={theme}
            trigger={
              <Icon
                ariaHidden
                glyph="gem-info-circle"
                height={Size.Icon}
                styleOverrides={{
                  container: styles.infoIcon({ theme }),
                }}
                tabIndex={0}
                width={Size.Icon}
              />
            }
          />
        </Conditional>
      </div>

      <span css={screenreaderOnly}>
        {[
          'Total',
          storyfirstUnitDisplayName(budgetType || context),
          `${budgetType ? 'allocation' : 'budget'}:`,
          formatCurrencyAmount({
            compactNotation: false,
            currency,
            value: max,
          }),
        ].join(' ')}
      </span>
      <span aria-hidden css={styles.max}>
        {formatBudget(max, currency)}
      </span>
    </div>
  </StrictMode>
)
BudgetHeader.displayName = 'Photon: BudgetHeader'
